import { doodBlockConfig, propertyValue } from "./dood-block-config";
import { cartPagePresetBlockBackgroundColorProperty } from "./cart-page-preset-block-background.util";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

/**
 * Serializable preview VM for `CartMerchantAmountBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type CartMerchantAmountBlockScenarioContent = {
  orderValidateData: {
    products: readonly {
      id: string;
      shop_id: string;
      name?: string;
      is_service_charge?: boolean;
      final_price?: number;
    }[];
    final_price: number;
    coupons_amount_off?: number;
    coupons?: readonly {
      code?: string;
      name?: string;
      type?: string;
      amount_off?: number;
      is_removable?: boolean;
    }[];
    use_points?: { amount?: number };
    tip?: { amount?: number };
    delivery_price_amount?: number;
    available_payment_methods: readonly unknown[];
  } | null;
  isOrderValidating: boolean;
  cartHasProduct: boolean;
  orderHasProduct: boolean;
};
export const CART_MERCHANT_AMOUNT_LEGACY_FIELD_ORDER = [
  { type: "heading", value: "Couleurs" },
  "heading_color",
  "text_color",
  "secondary_text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  cartMerchantAmountBlockDescriptor: _generatedCartMerchantAmountBlockDescriptor,
  cartMerchantAmountBlockBlocDefinition,
  cartMerchantAmountBlockDashboardLayout,
  cartMerchantAmountBlockConfig,
  cartMerchantAmountBlockResolveConfig,
  cartMerchantAmountBlockProperties,
  cartMerchantAmountBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Cart - Merchant amount",
    componentType: "CartMerchantAmountBlockComponent",
    category: "cart",
    description: "Cart totals block.",
    keywords: ["cart", "amount", "total"],
    allowedIn: [
      { bloc: 'CartPageComponent', slot: '*' },
    ],

    sharedFields: {},
  },
  () => ({
    background_color: cartPagePresetBlockBackgroundColorProperty(),
    heading_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: { label: "pages.blocks.cart-merchant-amount.heading-color.label" },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: { label: "pages.blocks.cart-merchant-amount.text-color.label" },
    }),
    secondary_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-500" },
      options: {
        label: "pages.blocks.cart-merchant-amount.secondary-text-color.label",
      },
    }),
  }),
  () => ({
    tabs: [
      {
        label: "pages.blocks.cart-merchant-amount.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.cart-page.fieldset.appearance.label",
            fields: ["background_color"],
          },
          {
            legend: "pages.blocks.cart-merchant-amount.fieldset.totals.label",
            fields: ["heading_color", "text_color", "secondary_text_color"],
          },
        ],
      },
    ],
  }),
);

export function cartMerchantAmountBlockDescriptor(): IBlockDescriptor;
export function cartMerchantAmountBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function cartMerchantAmountBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedCartMerchantAmountBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedCartMerchantAmountBlockDescriptor,
    cartMerchantAmountBlockProperties,
    fieldOrder,
  );
}

export {
  cartMerchantAmountBlockBlocDefinition,
  cartMerchantAmountBlockDashboardLayout,
  cartMerchantAmountBlockConfig,
  cartMerchantAmountBlockResolveConfig,
  cartMerchantAmountBlockProperties,
  cartMerchantAmountBlockDefinitionSetupScenarios,
};

export type CartMerchantAmountBlockConfig = typeof cartMerchantAmountBlockConfig;
